<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

// RED-317 §8 — the 15 counties × 8 regional purposes. A cell shows the
// freshness dot and period of the version a report dated today gets, "tuleb
// <date>" under it while a later version waits for its date, or "+ lisa"
// when the company has none yet; a click opens it in the drawer. The filter
// dims, never hides, so the grid keeps its shape.
const props = defineProps({
  counties: { type: Array, required: true },
  groups: { type: Array, required: true },
  purposes: { type: Array, required: true },
  overviewsByCell: { type: Object, required: true },
  selectedKey: { type: String, default: null },
  filter: { type: String, default: 'all' },
})

const emit = defineEmits(['select'])

const { t } = useI18n()

const groupSpans = computed(() =>
  props.groups
    .map(group => ({ ...group, span: props.purposes.filter(purpose => purpose.group === group.id).length }))
    .filter(group => group.span > 0),
)

/** The first purpose of each group draws the group's left rule. */
const startsGroup = index => index === 0 || props.purposes[index].group !== props.purposes[index - 1].group

const cellKey = (county, purpose) => `${county}|${purpose}`

const statusOf = (county, purpose) => props.overviewsByCell[cellKey(county, purpose)]?.status ?? 'missing'

const hasDraft = (county, purpose) => props.overviewsByCell[cellKey(county, purpose)]?.has_draft === true

const scheduledOf = (county, purpose) => props.overviewsByCell[cellKey(county, purpose)]?.scheduled ?? null

const formatDate = value => new Date(`${value}T00:00:00`).toLocaleDateString('et-EE')

const dimmed = status => (props.filter === 'stale' && status !== 'stale') || (props.filter === 'missing' && status !== 'missing')

// Literal class names, so Tailwind generates them.
const SPANS = ['', 'col-span-1', 'col-span-2', 'col-span-3', 'col-span-4', 'col-span-5', 'col-span-6', 'col-span-7', 'col-span-8']

const DOT = { fresh: 'bg-green-600', stale: 'bg-amber-500' }
const TEXT = { fresh: 'text-green-700', stale: 'text-amber-700' }

const cellClass = (county, purpose) => {
  const status = statusOf(county, purpose)
  const selected = props.selectedKey === cellKey(county, purpose)
  return [
    selected
      ? 'border-2 border-primary-500 bg-primary-50'
      : status === 'missing'
        ? 'border border-dashed border-gray-200 bg-gray-50 hover:border-gray-300'
        : 'border border-gray-200 bg-white hover:border-gray-300',
    dimmed(status) ? 'opacity-25' : '',
  ]
}
</script>

<template>
  <div class="overflow-x-auto rounded-lg border border-gray-200 bg-white" data-testid="mo-matrix">
    <div class="grid min-w-[56rem] grid-cols-[minmax(7rem,9rem)_repeat(8,minmax(0,1fr))]">
      <div class="border-b border-gray-200" />
      <div
        v-for="group in groupSpans"
        :key="group.id"
        class="border-b border-l border-gray-200 px-2.5 pt-2.5 pb-1 text-xs font-semibold tracking-wide text-gray-500 uppercase"
        :class="SPANS[group.span]"
      >
        {{ group.label }}
      </div>

      <div class="border-b border-gray-200 px-3 py-1.5 text-xs text-gray-500">
        {{ $t('messages.admin.market_overviews.matrix.county') }}
      </div>
      <div
        v-for="(purpose, index) in purposes"
        :key="purpose.id"
        class="border-b border-gray-200 px-2 pt-1 pb-2 text-xs leading-tight font-medium text-gray-700"
        :class="{ 'border-l': startsGroup(index) }"
      >
        {{ purpose.label }}
      </div>

      <template v-for="county in counties" :key="county.value">
        <div class="flex h-11 items-center border-b border-gray-200 px-3">
          <span class="truncate text-sm font-medium text-gray-800">{{ county.label }}</span>
        </div>
        <div
          v-for="(purpose, index) in purposes"
          :key="purpose.id"
          class="border-b border-gray-200 p-1"
          :class="{ 'border-l': startsGroup(index) }"
        >
          <button
            type="button"
            class="flex h-9 w-full cursor-pointer flex-col items-start justify-center rounded-sm px-2 text-left transition-colors"
            :class="cellClass(county.value, purpose.id)"
            :aria-label="t('messages.admin.market_overviews.matrix.cell_label', { county: county.label, purpose: purpose.label })"
            :aria-pressed="selectedKey === cellKey(county.value, purpose.id)"
            :data-testid="`mo-cell-${county.value}-${purpose.id}`"
            :data-status="statusOf(county.value, purpose.id)"
            :data-draft="hasDraft(county.value, purpose.id) ? 'true' : undefined"
            :data-scheduled="scheduledOf(county.value, purpose.id)?.valid_from"
            @click="emit('select', { county: county.value, purpose: purpose.id })"
          >
            <span v-if="statusOf(county.value, purpose.id) === 'missing'" class="text-xs" :class="hasDraft(county.value, purpose.id) ? 'text-primary-700' : 'text-gray-400'">
              <template v-if="hasDraft(county.value, purpose.id)"><i class="pi pi-pencil text-[0.6rem]" /> {{ $t('messages.admin.market_overviews.matrix.draft') }}</template>
              <template v-else>{{ $t('messages.admin.market_overviews.matrix.add') }}</template>
            </span>
            <span
              v-else
              class="inline-flex items-center gap-1.5 text-xs font-medium whitespace-nowrap"
              :class="TEXT[statusOf(county.value, purpose.id)]"
            >
              <span class="inline-block size-1.5 shrink-0 rounded-full" :class="DOT[statusOf(county.value, purpose.id)]" />
              {{ overviewsByCell[cellKey(county.value, purpose.id)].live?.period }}
              <i
                v-if="hasDraft(county.value, purpose.id)"
                class="pi pi-pencil text-[0.6rem] text-primary-700"
                :title="$t('messages.admin.market_overviews.matrix.draft')"
              />
            </span>
            <span v-if="scheduledOf(county.value, purpose.id)" class="max-w-full truncate text-[0.65rem] leading-tight text-sky-700">
              {{ $t('messages.admin.market_overviews.status.scheduled', { date: formatDate(scheduledOf(county.value, purpose.id).valid_from) }) }}
            </span>
          </button>
        </div>
      </template>
    </div>
  </div>
</template>
